/* ---------------------------------------------------------------------------
 * Channel manager integrations directory.
 *
 * Curated, industry-standard list of channels Prostay supports through the
 * Channel Manager. Used by the searchable directory on /channel-manager/.
 *
 * Categories:
 *   ota              - online travel agency (rooms + packages)
 *   wholesaler       - B2B bedbank / wholesaler
 *   gds              - global distribution system
 *   metasearch       - rate aggregator / meta search
 *   tour-operator    - tour operator package distribution
 *   vacation-rental  - alternative-accommodation OTA
 *   hostel           - hostel / budget-only OTA
 *   corporate        - corporate / loyalty / TMC distribution
 *
 * Regions:
 *   global, europe, asia-pacific, americas, middle-east, africa, russia-cis
 * ------------------------------------------------------------------------ */

export const CHANNEL_CATEGORIES = [
  {id: 'ota',             label: 'OTA'},
  {id: 'wholesaler',      label: 'Wholesaler'},
  {id: 'gds',             label: 'GDS'},
  {id: 'metasearch',      label: 'Metasearch'},
  {id: 'tour-operator',   label: 'Tour operator'},
  {id: 'vacation-rental', label: 'Vacation rental'},
  {id: 'hostel',          label: 'Hostel'},
  {id: 'corporate',       label: 'Corporate'}
];

export const CHANNEL_REGIONS = [
  {id: 'global',       label: 'Global'},
  {id: 'europe',       label: 'Europe'},
  {id: 'asia-pacific', label: 'Asia-Pacific'},
  {id: 'americas',     label: 'Americas'},
  {id: 'middle-east',  label: 'Middle East'},
  {id: 'africa',       label: 'Africa'},
  {id: 'russia-cis',   label: 'Russia & CIS'}
];

export const CHANNEL_INTEGRATIONS = [
  // -------- Global OTAs (the ones every property cares about) -----------
  {name: 'Booking.com',                category: 'ota', region: 'global'},
  {name: 'Expedia',                    category: 'ota', region: 'global'},
  {name: 'Hotels.com',                 category: 'ota', region: 'global'},
  {name: 'Agoda',                      category: 'ota', region: 'global'},
  {name: 'Airbnb',                     category: 'vacation-rental', region: 'global'},
  {name: 'Vrbo',                       category: 'vacation-rental', region: 'global'},
  {name: 'Trip.com',                   category: 'ota', region: 'global'},
  {name: 'Hostelworld',                category: 'hostel', region: 'global'},
  {name: 'Tripadvisor',                category: 'metasearch', region: 'global'},
  {name: 'Google Hotel Ads',           category: 'metasearch', region: 'global'},
  {name: 'Trivago',                    category: 'metasearch', region: 'global'},
  {name: 'Kayak',                      category: 'metasearch', region: 'global'},
  {name: 'Skyscanner',                 category: 'metasearch', region: 'global'},
  {name: 'HotelsCombined',             category: 'metasearch', region: 'global'},
  {name: 'Wego',                       category: 'metasearch', region: 'middle-east'},
  {name: 'Priceline',                  category: 'ota', region: 'americas'},
  {name: 'Hotwire',                    category: 'ota', region: 'americas'},
  {name: 'Orbitz',                     category: 'ota', region: 'americas'},
  {name: 'Travelocity',                category: 'ota', region: 'americas'},
  {name: 'CheapTickets',               category: 'ota', region: 'americas'},
  {name: 'Wotif',                      category: 'ota', region: 'asia-pacific'},
  {name: 'ebookers',                   category: 'ota', region: 'europe'},
  {name: 'lastminute.com',             category: 'ota', region: 'europe'},
  {name: 'HotelTonight',               category: 'ota', region: 'global'},
  {name: 'Mr & Mrs Smith',             category: 'ota', region: 'global'},
  {name: 'Tablet Hotels',              category: 'ota', region: 'global'},
  {name: 'Splendia',                   category: 'ota', region: 'europe'},
  {name: 'i-escape',                   category: 'ota', region: 'europe'},
  {name: 'The Plum Guide',             category: 'vacation-rental', region: 'global'},
  {name: 'Booking.com Homes & Apartments', category: 'vacation-rental', region: 'global'},
  {name: 'Marriott Homes & Villas',    category: 'vacation-rental', region: 'global'},

  // -------- European OTAs -----------------------------------------------
  {name: 'HRS',                        category: 'ota', region: 'europe'},
  {name: 'Hotel.de',                   category: 'ota', region: 'europe'},
  {name: 'Atrapalo',                   category: 'ota', region: 'europe'},
  {name: 'eDreams',                    category: 'ota', region: 'europe'},
  {name: 'ODIGEO',                     category: 'ota', region: 'europe'},
  {name: 'Logitravel',                 category: 'ota', region: 'europe'},
  {name: 'HotelSpecials',              category: 'ota', region: 'europe'},
  {name: 'Otel.com',                   category: 'ota', region: 'europe'},
  {name: 'Voyage Privé',               category: 'ota', region: 'europe'},
  {name: 'Booking.fr',                 category: 'ota', region: 'europe'},
  {name: 'Holiday Pirates',            category: 'ota', region: 'europe'},
  {name: 'Centerparcs',                category: 'tour-operator', region: 'europe'},
  {name: 'Snaptrip',                   category: 'vacation-rental', region: 'europe'},
  {name: 'Holidu',                     category: 'vacation-rental', region: 'europe'},
  {name: 'Rentalia',                   category: 'vacation-rental', region: 'europe'},
  {name: 'fewo-direkt',                category: 'vacation-rental', region: 'europe'},
  {name: 'Interhome',                  category: 'vacation-rental', region: 'europe'},
  {name: 'Booking.it',                 category: 'ota', region: 'europe'},
  {name: 'Volagratis',                 category: 'ota', region: 'europe'},
  {name: 'Bravofly',                   category: 'ota', region: 'europe'},
  {name: 'Abritel',                    category: 'vacation-rental', region: 'europe'},

  // -------- Asia-Pacific OTAs -------------------------------------------
  {name: 'Ctrip',                      category: 'ota', region: 'asia-pacific'},
  {name: 'Tujia',                      category: 'vacation-rental', region: 'asia-pacific'},
  {name: 'Mafengwo',                   category: 'ota', region: 'asia-pacific'},
  {name: 'Qunar',                      category: 'ota', region: 'asia-pacific'},
  {name: 'Fliggy',                     category: 'ota', region: 'asia-pacific'},
  {name: 'Tongcheng-Elong',            category: 'ota', region: 'asia-pacific'},
  {name: 'Lvmama',                     category: 'ota', region: 'asia-pacific'},
  {name: 'Meituan',                    category: 'ota', region: 'asia-pacific'},
  {name: 'AirAsia',                    category: 'ota', region: 'asia-pacific'},
  {name: 'Klook',                      category: 'ota', region: 'asia-pacific'},
  {name: 'Traveloka',                  category: 'ota', region: 'asia-pacific'},
  {name: 'Tiket.com',                  category: 'ota', region: 'asia-pacific'},
  {name: 'Pegipegi',                   category: 'ota', region: 'asia-pacific'},
  {name: 'RedDoorz',                   category: 'ota', region: 'asia-pacific'},
  {name: 'Naver',                      category: 'ota', region: 'asia-pacific'},
  {name: 'Yanolja',                    category: 'ota', region: 'asia-pacific'},
  {name: 'GoodChoice',                 category: 'ota', region: 'asia-pacific'},
  {name: 'Jalan.net',                  category: 'ota', region: 'asia-pacific'},
  {name: 'Rakuten Travel',             category: 'ota', region: 'asia-pacific'},
  {name: 'Rurubu Travel',              category: 'ota', region: 'asia-pacific'},
  {name: 'Yahoo! Travel',              category: 'ota', region: 'asia-pacific'},
  {name: 'JTB',                        category: 'tour-operator', region: 'asia-pacific'},
  {name: 'HIS Travel',                 category: 'tour-operator', region: 'asia-pacific'},
  {name: 'KKday',                      category: 'ota', region: 'asia-pacific'},
  {name: 'Mytour',                     category: 'ota', region: 'asia-pacific'},
  {name: 'ZenHotels',                  category: 'ota', region: 'asia-pacific'},
  {name: 'MakeMyTrip',                 category: 'ota', region: 'asia-pacific'},
  {name: 'Goibibo',                    category: 'ota', region: 'asia-pacific'},
  {name: 'Cleartrip',                  category: 'ota', region: 'asia-pacific'},
  {name: 'Yatra',                      category: 'ota', region: 'asia-pacific'},
  {name: 'EaseMyTrip',                 category: 'ota', region: 'asia-pacific'},
  {name: 'Ixigo',                      category: 'ota', region: 'asia-pacific'},
  {name: 'OYO',                        category: 'ota', region: 'asia-pacific'},
  {name: 'Stayflexi',                  category: 'ota', region: 'asia-pacific'},
  {name: 'Webjet',                     category: 'ota', region: 'asia-pacific'},
  {name: 'Stayz',                      category: 'vacation-rental', region: 'asia-pacific'},
  {name: 'Booking.com Korea',          category: 'ota', region: 'asia-pacific'},

  // -------- Latin American OTAs -----------------------------------------
  {name: 'Despegar',                   category: 'ota', region: 'americas'},
  {name: 'Decolar.com',                category: 'ota', region: 'americas'},
  {name: 'Almundo',                    category: 'ota', region: 'americas'},
  {name: 'Avantrip',                   category: 'ota', region: 'americas'},
  {name: 'Hotelopia',                  category: 'ota', region: 'americas'},
  {name: 'Yorento',                    category: 'ota', region: 'americas'},
  {name: 'HotelDO',                    category: 'ota', region: 'americas'},
  {name: 'Best Day Travel',            category: 'tour-operator', region: 'americas'},
  {name: 'Submarino Viagens',          category: 'ota', region: 'americas'},
  {name: 'CVC',                        category: 'tour-operator', region: 'americas'},
  {name: 'Hoteis.com (BR)',            category: 'ota', region: 'americas'},
  {name: 'Maxmilhas',                  category: 'ota', region: 'americas'},

  // -------- Middle East / North Africa ---------------------------------
  {name: 'Almosafer',                  category: 'ota', region: 'middle-east'},
  {name: 'Tajawal',                    category: 'ota', region: 'middle-east'},
  {name: 'Rehlat',                     category: 'ota', region: 'middle-east'},
  {name: 'Yenibooking',                category: 'ota', region: 'middle-east'},
  {name: 'Booking.ae',                 category: 'ota', region: 'middle-east'},
  {name: 'Holidaycheck',               category: 'metasearch', region: 'europe'},
  {name: 'Atravo',                     category: 'ota', region: 'middle-east'},
  {name: 'Onatera Travel',             category: 'ota', region: 'middle-east'},

  // -------- Africa ------------------------------------------------------
  {name: 'Jumia Travel',               category: 'ota', region: 'africa'},
  {name: 'Hotels.ng',                  category: 'ota', region: 'africa'},
  {name: 'Travelstart',                category: 'ota', region: 'africa'},
  {name: 'NightsBridge',               category: 'ota', region: 'africa'},
  {name: 'Booking.co.za',              category: 'ota', region: 'africa'},

  // -------- Russia / CIS ------------------------------------------------
  {name: 'Ostrovok',                   category: 'ota', region: 'russia-cis'},
  {name: 'Bronevik',                   category: 'ota', region: 'russia-cis'},
  {name: 'OneTwoTrip',                 category: 'ota', region: 'russia-cis'},
  {name: '101Hotels',                  category: 'ota', region: 'russia-cis'},
  {name: 'Tripster',                   category: 'ota', region: 'russia-cis'},
  {name: 'Yandex.Travel',              category: 'ota', region: 'russia-cis'},

  // -------- Wholesalers / bedbanks -------------------------------------
  {name: 'Hotelbeds',                  category: 'wholesaler', region: 'global'},
  {name: 'Bedsonline',                 category: 'wholesaler', region: 'global'},
  {name: 'WebBeds',                    category: 'wholesaler', region: 'global'},
  {name: 'GTA Travel',                 category: 'wholesaler', region: 'global'},
  {name: 'Miki Travel',                category: 'wholesaler', region: 'asia-pacific'},
  {name: 'TBO Holidays',               category: 'wholesaler', region: 'global'},
  {name: 'Travco',                     category: 'wholesaler', region: 'middle-east'},
  {name: 'Sunhotels',                  category: 'wholesaler', region: 'europe'},
  {name: 'Restel',                     category: 'wholesaler', region: 'europe'},
  {name: 'JacTravel',                  category: 'wholesaler', region: 'europe'},
  {name: 'MTS Globe',                  category: 'wholesaler', region: 'europe'},
  {name: 'Stuba',                      category: 'wholesaler', region: 'europe'},
  {name: 'Dida Travel',                category: 'wholesaler', region: 'asia-pacific'},
  {name: 'Welcomebeds',                category: 'wholesaler', region: 'europe'},
  {name: 'Travel Republic',            category: 'wholesaler', region: 'europe'},
  {name: 'Travel2',                    category: 'wholesaler', region: 'europe'},
  {name: 'Travelopro',                 category: 'wholesaler', region: 'asia-pacific'},
  {name: 'Tourico Holidays',           category: 'wholesaler', region: 'global'},
  {name: 'Traveltek',                  category: 'wholesaler', region: 'europe'},
  {name: 'Methatours',                 category: 'wholesaler', region: 'middle-east'},

  // -------- GDS ---------------------------------------------------------
  {name: 'Amadeus',                    category: 'gds', region: 'global'},
  {name: 'Sabre',                      category: 'gds', region: 'global'},
  {name: 'Travelport / Galileo',       category: 'gds', region: 'global'},
  {name: 'Travelport / Worldspan',     category: 'gds', region: 'global'},
  {name: 'Travelport / Apollo',        category: 'gds', region: 'global'},
  {name: 'Pegasus',                    category: 'gds', region: 'global'},

  // -------- Tour operators / leisure groups -----------------------------
  {name: 'TUI',                        category: 'tour-operator', region: 'europe'},
  {name: 'DER Touristik',              category: 'tour-operator', region: 'europe'},
  {name: 'FTI Group',                  category: 'tour-operator', region: 'europe'},
  {name: 'Thomas Cook',                category: 'tour-operator', region: 'europe'},
  {name: 'Jet2Holidays',               category: 'tour-operator', region: 'europe'},
  {name: 'On The Beach',               category: 'tour-operator', region: 'europe'},
  {name: 'Loveholidays',               category: 'tour-operator', region: 'europe'},
  {name: 'Hotelplan',                  category: 'tour-operator', region: 'europe'},
  {name: 'Olympia Holidays',           category: 'tour-operator', region: 'europe'},
  {name: 'Ving',                       category: 'tour-operator', region: 'europe'},
  {name: 'Apollo Travel',              category: 'tour-operator', region: 'europe'},
  {name: 'TUIfly',                     category: 'tour-operator', region: 'europe'},
  {name: 'Iberostar Wholesale',        category: 'tour-operator', region: 'europe'},

  // -------- Vacation rental specialists --------------------------------
  {name: 'HomeToGo',                   category: 'vacation-rental', region: 'global'},
  {name: 'FlipKey',                    category: 'vacation-rental', region: 'global'},
  {name: 'HouseTrip',                  category: 'vacation-rental', region: 'europe'},
  {name: 'Wimdu',                      category: 'vacation-rental', region: 'europe'},
  {name: 'AltoVita Stays',             category: 'vacation-rental', region: 'global'},
  {name: 'Sonder',                     category: 'vacation-rental', region: 'global'},
  {name: 'Blueground',                 category: 'vacation-rental', region: 'global'},
  {name: 'Hipcamp',                    category: 'vacation-rental', region: 'americas'},
  {name: 'Glamping Hub',               category: 'vacation-rental', region: 'global'},

  // -------- Hostels / budget -------------------------------------------
  {name: 'HostelBookers',              category: 'hostel', region: 'global'},
  {name: 'Hostelz',                    category: 'hostel', region: 'global'},
  {name: 'Booking.com Hostels',        category: 'hostel', region: 'global'},
  {name: 'Gomio',                      category: 'hostel', region: 'europe'},

  // -------- Corporate / loyalty / TMC ----------------------------------
  {name: 'American Express GBT',       category: 'corporate', region: 'global'},
  {name: 'BCD Travel',                 category: 'corporate', region: 'global'},
  {name: 'CWT',                        category: 'corporate', region: 'global'},
  {name: 'Egencia',                    category: 'corporate', region: 'global'},
  {name: 'Booking.com for Business',   category: 'corporate', region: 'global'},
  {name: 'Navan',                      category: 'corporate', region: 'global'},
  {name: 'TripActions',                category: 'corporate', region: 'global'},
  {name: 'HRS Group Corporate',        category: 'corporate', region: 'europe'},
  {name: 'Lola',                       category: 'corporate', region: 'americas'}
];

/* ---------------------------------------------------------------------------
 * Helper: case- and accent-insensitive substring match.
 *   "atrapalo" matches "Atrápalo"
 *   "BOOKING" matches "Booking.com"
 * ------------------------------------------------------------------------ */
export function normalize(s) {
  return (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}
